import { Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import { CombinedFormShape } from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

import { useUnguardedDispositiveProvisionsContext } from '../../contexts/dispositiveProvisions.context';
import {
  ASSET_OPTION_GROUP_NAMES,
  AssetOptionPrefix,
  buildCompositeAssetValues,
  distributeAssetOptionValues,
  parseAssetOptionValue,
  useEntityAssetOptions,
} from '../hooks/useEntityAssetOptions';
import { useHouseholdAssetsForCrossEntityDp } from '../hooks/useHouseholdAssetsForCrossEntityDp';
import {
  applyAccountCascade,
  buildDrawerAssetGroups,
  collapseStaticsToGroups,
  expandSelectedGroupsToStatics,
  filterAssetOptionsWithAccounts,
  moveSelectedOptionsToGroupTop,
  resolveSelectedAssetOptions,
} from './AssetSelectorDropdown.utils';
import { RecipientsBasePath } from './DispositiveProvisionsRecipient';
import { useSetRecipientField } from './hooks/useSetRecipientField';
import { ViewAllAssetsToDistributeSideDrawer } from './ViewAllAssetsToDistributeSideDrawer';

interface AssetSelectorDropdownProps {
  recipientsBasePath: RecipientsBasePath;
  idx: number;
}

// Group labels that disambiguate the inline dropdown (the dispositioning
// entity's own assets) from the household-wide "View all assets to
// distribute" drawer.
const SELECTED_ENTITY_GROUP_LABELS: Record<string, string> = {
  [ASSET_OPTION_GROUP_NAMES.BUSINESSES]: "Selected entity's businesses",
  [ASSET_OPTION_GROUP_NAMES.ASSETS]: 'Assets owned by this entity',
  [ASSET_OPTION_GROUP_NAMES.LIFE_INSURANCE]: "Selected entity's life insurance",
};

// Group label for the selected assets that other holders in the household
// own. The inline dropdown lists them after the current entity's groups.
const OTHER_ASSETS_GROUP_LABEL = 'Other assets';

/**
 * Multi-select dropdown for choosing specific assets in an asset-level DP.
 * Reads from and writes to the 4 separate asset ID form fields
 * (assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds).
 *
 * The inline dropdown lists the dispositioning entity's own assets. A footer
 * button opens a side drawer that lists the dispositioning entity's assets
 * first, then the assets of all other holders in the household, so the user
 * can build cross-entity asset DPs at either death event (e.g. a Rev trust at
 * 1st death receiving assets via a PO Will).
 */
export function AssetSelectorDropdown({
  recipientsBasePath,
  idx,
}: AssetSelectorDropdownProps) {
  // The DP context provides the *currently-edited disposing target* id, which
  // can change as the user clicks around the diagram. When this component
  // is rendered outside that context (e.g. the beneficiary designations
  // form, which has no diagram-driven target switching), fall back to the
  // URL-bound entity from the entity details context.
  const dpContext = useUnguardedDispositiveProvisionsContext();
  const { entityId: entityDetailsEntityId } = useEntityDetailsContext();
  const { householdId: householdContextId } = useHouseholdDetailsContext();
  const entityId = dpContext.id ?? entityDetailsEntityId ?? '';
  const householdId = dpContext.householdId ?? householdContextId;
  const { control } = useFormContext<CombinedFormShape>();
  const {
    options: entityOptions,
    holderDisplayName: entityDisplayName,
    loading: entityOptionsLoading,
  } = useEntityAssetOptions(entityId, {
    groupByIntegratedAccount: true,
  });
  // Flat option pool (same query, Apollo-cached) used only to resolve a legacy
  // DP that already targets a non-integrated asset group — those rows aren't in
  // the grouped list, so without this the selection would silently drop on save.
  const { options: flatEntityOptions } = useEntityAssetOptions(entityId);
  const {
    groups: householdGroups,
    allOptions: householdAllOptions,
    loading: householdLoading,
  } = useHouseholdAssetsForCrossEntityDp({
    householdId,
    excludeEntityId: entityId,
    groupByIntegratedAccount: true,
  });
  const setRecipientField = useSetRecipientField(recipientsBasePath, idx);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Inline options: current entity's assets. Relabel the group names so the
  // user knows the inline list is scoped to the selected entity (the
  // cross-entity drawer covers the rest of the household).
  const inlineOptions = useMemo<TypeaheadSelectInputOption<string>[]>(() => {
    return entityOptions.map((opt) => {
      const renamed = opt.groupName
        ? SELECTED_ENTITY_GROUP_LABELS[opt.groupName]
        : undefined;
      return renamed ? { ...opt, groupName: renamed } : opt;
    });
  }, [entityOptions]);

  // Account parent rows (one per integrated/linked account); toggling one
  // cascades to its child statics. Household parents come from other entities'
  // accounts surfaced in the drawer; both are needed to expand/collapse a
  // stored group selection from either side.
  const inlineParentOptions = useMemo(
    () => inlineOptions.filter((opt) => opt.isParentRow),
    [inlineOptions]
  );
  const allParentOptions = useMemo(
    () => [
      ...inlineParentOptions,
      ...householdAllOptions.filter((opt) => opt.isParentRow),
    ],
    [inlineParentOptions, householdAllOptions]
  );

  const [assetStaticIds, assetGroupIds, insurancePolicyIds, ownershipStakeIds] =
    useWatch({
      control,
      name: [
        `${recipientsBasePath}.${idx}.assetStaticIds` as const,
        `${recipientsBasePath}.${idx}.assetGroupIds` as const,
        `${recipientsBasePath}.${idx}.insurancePolicyIds` as const,
        `${recipientsBasePath}.${idx}.ownershipStakeIds` as const,
      ],
    });

  // Internal selection rep is leaf statics: a stored account `assetGroup:<id>`
  // is expanded into its holdings so the multiselect renders them (and the
  // derived parent checkbox) as selected, and the cascade keeps working
  // unchanged. The group<->statics decision is re-applied on write.
  const compositeValues = useMemo(
    () =>
      expandSelectedGroupsToStatics(
        buildCompositeAssetValues({
          assetStaticIds: assetStaticIds as string[],
          assetGroupIds: assetGroupIds as string[],
          insurancePolicyIds: insurancePolicyIds as string[],
          ownershipStakeIds: ownershipStakeIds as string[],
        }),
        allParentOptions
      ),
    [
      assetStaticIds,
      assetGroupIds,
      insurancePolicyIds,
      ownershipStakeIds,
      allParentOptions,
    ]
  );

  // A legacy DP may target a non-integrated asset group (e.g. a manually
  // created group) that the grouped list no longer shows. Surface only the
  // currently-selected ones, from the flat pool, so they still render and can
  // be deselected — without re-cluttering the list with unselected groups.
  const legacyGroupOptions = useMemo(() => {
    const inlineValues = new Set(inlineOptions.map((o) => o.value));
    const selected = new Set(compositeValues);
    return flatEntityOptions
      .filter(
        (opt) =>
          parseAssetOptionValue(opt.value).prefix ===
            AssetOptionPrefix.AssetGroup &&
          !inlineValues.has(opt.value) &&
          selected.has(opt.value)
      )
      .map((opt) => {
        const renamed = opt.groupName
          ? SELECTED_ENTITY_GROUP_LABELS[opt.groupName]
          : undefined;
        return renamed ? { ...opt, groupName: renamed } : opt;
      });
  }, [flatEntityOptions, inlineOptions, compositeValues]);

  const currentEntityOptions = useMemo(
    () => [...inlineOptions, ...legacyGroupOptions],
    [inlineOptions, legacyGroupOptions]
  );

  // Selected assets that other holders own. The inline dropdown shows them in
  // their own group so that the user can see and clear them without the
  // drawer. The hierarchy fields are removed because their account parent rows
  // are not in this list.
  const otherSelectedOptions = useMemo(() => {
    const currentEntityValues = new Set(
      currentEntityOptions.map((o) => o.value)
    );
    const selected = new Set(compositeValues);
    return householdAllOptions
      .filter(
        (opt) =>
          !opt.isParentRow &&
          selected.has(opt.value) &&
          !currentEntityValues.has(opt.value)
      )
      .map((opt) => ({
        ...opt,
        groupName: OTHER_ASSETS_GROUP_LABEL,
        indentLevel: undefined,
        parentValue: undefined,
      }));
  }, [currentEntityOptions, compositeValues, householdAllOptions]);

  // Selected options go to the top of their own group. The dropdown does not
  // use a separate "Selected" group (see groupSelectedOptions below).
  const listOptions = useMemo(
    () =>
      moveSelectedOptionsToGroupTop(
        [...currentEntityOptions, ...otherSelectedOptions],
        compositeValues
      ),
    [currentEntityOptions, otherSelectedOptions, compositeValues]
  );

  // The drawer reuses the inline options for the current entity so that a
  // selection in the drawer and in the inline dropdown use the same values.
  const drawerGroups = useMemo(
    () =>
      buildDrawerAssetGroups(
        {
          entityId,
          // The name and the options come from the same query, so the name is
          // loaded when the group has options to show.
          entityName: `${entityDisplayName ?? ''} (this entity)`,
          options: currentEntityOptions,
        },
        householdGroups
      ),
    [entityId, entityDisplayName, currentEntityOptions, householdGroups]
  );

  const selectedOptions = useMemo(
    () =>
      resolveSelectedAssetOptions(
        compositeValues,
        listOptions,
        householdAllOptions
      ),
    [compositeValues, listOptions, householdAllOptions]
  );

  const writeSelection = useCallback(
    (newCompositeValues: string[]) => {
      // Collapse fully-selected accounts back into their `assetGroup:<id>`
      // (a live group reference) before persisting; partial accounts stay as
      // individual statics.
      const distributed = distributeAssetOptionValues(
        collapseStaticsToGroups(newCompositeValues, allParentOptions)
      );
      const opts = { shouldDirty: true } as const;
      setRecipientField('assetStaticIds', distributed.assetStaticIds, opts);
      setRecipientField('assetGroupIds', distributed.assetGroupIds, opts);
      setRecipientField(
        'insurancePolicyIds',
        distributed.insurancePolicyIds,
        opts
      );
      setRecipientField(
        'ownershipStakeIds',
        distributed.ownershipStakeIds,
        opts
      );
    },
    [setRecipientField, allParentOptions]
  );

  const handleChange = useCallback(
    (_event: unknown, newValues: Array<{ value: string }>) => {
      // Toggling an account parent cascades to its child statics and strips the
      // parent's own value, so the live selection stays leaf statics.
      const cascaded = applyAccountCascade(
        newValues.map((v) => v.value),
        inlineParentOptions
      );
      const cascadedSet = new Set(cascaded);
      // While the household-wide query is in flight, saved cross-entity
      // picks aren't yet resolvable into chips, so MUI Autocomplete can't
      // include them in `newValues`. Carry any unresolved composite values
      // forward so an unrelated chip removal during the loading window
      // doesn't silently overwrite them out of the form.
      if (householdLoading) {
        const knownValues = new Set([
          ...listOptions.map((o) => o.value),
          ...householdAllOptions.map((o) => o.value),
        ]);
        const unresolvedCarryOver = compositeValues.filter(
          (v) => !knownValues.has(v) && !cascadedSet.has(v)
        );
        writeSelection([...cascaded, ...unresolvedCarryOver]);
        return;
      }
      writeSelection(cascaded);
    },
    [
      writeSelection,
      householdLoading,
      listOptions,
      inlineParentOptions,
      householdAllOptions,
      compositeValues,
    ]
  );

  const [inputValue, setInputValue] = useState('');

  const loading = entityOptionsLoading || householdLoading;
  const hasInlineOptions = inlineOptions.length > 0;
  const showEmptyPlaceholder = !loading && !hasInlineOptions;

  // Memoize so TypeaheadMultiSelectInput's PaperComponent (memoized on
  // popperFooter identity) doesn't churn on every keystroke during typeahead
  // filtering, which would remount the popper option list.
  const popperFooter = useMemo(
    () => (
      <Button
        variant="secondary"
        size="xs"
        fullWidth
        onClick={() => setIsDrawerOpen(true)}
      >
        View all assets in this household
      </Button>
    ),
    []
  );

  return (
    <>
      <TypeaheadMultiSelectInput
        label="Assets"
        options={listOptions}
        values={selectedOptions}
        onChange={handleChange}
        filterOptions={filterAssetOptionsWithAccounts}
        loading={loading}
        placeholder={
          showEmptyPlaceholder ? 'No assets available' : 'Select assets'
        }
        noOptionsText={
          <Typography variant="body-xs">No assets available</Typography>
        }
        inlineEndLabel
        inputValue={inputValue}
        onInputChange={(value) => setInputValue(value)}
        popperFooter={popperFooter}
        groupSelectedOptions={false}
      />
      {isDrawerOpen && (
        <ViewAllAssetsToDistributeSideDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          groups={drawerGroups}
          selectedValues={compositeValues}
          onApply={writeSelection}
          loading={loading}
        />
      )}
    </>
  );
}
